import React, { useState, useEffect, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

moment.locale('fr');
const localizer = momentLocalizer(moment);

function CalendarPage() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const modalRef = useRef();

  useEffect(() => {
    axios.get('https://www.clublfit.org/api/evenements')
      .then(response => {
        const transformedEvents = response.data.flatMap(event => {
        const redondant = parseInt(event.redondant_Evenement); // S'assure que c'est un nombre
        const start = new Date(event.Date_Debut);
        const end = new Date(event.Date_Fin || event.Date_Debut);

        const eventDays = [];

        if (redondant === 1 && event.Jour) {
          // Cas 1 : événement redondant → chaque jour correspondant
          const daysMap = { 'Dimanche': 0, 'Lundi': 1, 'Mardi': 2, 'Mercredi': 3, 'Jeudi': 4, 'Vendredi': 5, 'Samedi': 6 };
          const targetDay = daysMap[event.Jour];

          let current = new Date(start);
          while (current <= end) {
            if (current.getDay() === targetDay) {
              const startTime = new Date(current);
              const endTime = new Date(current);
              startTime.setHours(...event.Heure_Debut.split(':'));
              endTime.setHours(...event.Heure_Fin.split(':'));

              eventDays.push({
                title: `${event.Nom_Evenement} (${event.Lieu})`,
                description: event.Description_Evenement || 'Aucune description disponible',
                start: startTime,
                end: endTime,
                allDay: false,
                color: event.Couleur || '#007bff',
              });
            }
            current.setDate(current.getDate() + 1);
          }

        } else {
          // Cas 2 : événement non redondant → tous les jours entre Date_Debut et Date_Fin
          let current = new Date(start);
          while (current <= end) {
            const startTime = new Date(current);
            const endTime = new Date(current);
            startTime.setHours(...event.Heure_Debut.split(':'));
            endTime.setHours(...event.Heure_Fin.split(':'));

            eventDays.push({
              title: `${event.Nom_Evenement} (${event.Lieu})`,
              description: event.Description_Evenement || 'Aucune description disponible',
              start: startTime,
              end: endTime,
              allDay: false,
              color: event.Couleur || '#007bff',
            });

            current.setDate(current.getDate() + 1);
          }
        }

        return eventDays;
      });

        setEvents(transformedEvents);
      })
      .catch(error => console.error('Erreur lors de la récupération des événements', error));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSelectedEvent(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setSelectedEvent]);

  const messages = {
    allDay: t('allDay'),
    previous: t('previous'),
    next: t('next'),
    today: t('today'),
    month: t('month'),
    week: t('week'),
    day: t('day'),
    agenda: t('agenda'),
    work_week: t('work_week'),
    date: t('date'),
    time: t('time'),
    event: t('event'),
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color,
        border: 'none',
        borderRadius: '8px',
        color: 'white',
        padding: '5px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        fontWeight: '500',
        transition: 'transform 0.2s ease-in-out',
      },
      title: event.title,
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  return (
    <div className="container my-0">
      <h2 className="text-center mb-4">{t('Calendrier')}</h2>
      <div className="row justify-content-center">
        <div className="col-lg-12">
          <div style={{ height: 600, background: '#f9f9f9', borderRadius: '10px', padding: '0px' }} className="shadow-sm">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%', borderRadius: '10px' }}
              messages={messages}
              min={new Date(1970, 1, 1, 8)}
              max={new Date(1970, 1, 1, 20)}
              eventPropGetter={eventStyleGetter}
              popup
              onSelectEvent={handleSelectEvent}
              views={['work_week', 'day', 'month', 'agenda']}
              defaultView="work_week"
            />
          </div>
        </div>
      </div>

      {selectedEvent && (
        <div className="modal-overlay fade-in" style={overlayStyle}>
          <div className="modal-dialog scale-in" style={dialogStyle} ref={modalRef}>
            <div className="modal-content" style={contentStyle}>
              <div className="modal-header" style={headerStyle}>
                <h5 className="modal-title" style={titleStyle}>{selectedEvent.title}</h5>
                <button type="button" className="close" onClick={() => setSelectedEvent(null)} style={closeButtonStyle}>&times;</button>
              </div>
              <div className="modal-body" style={bodyStyle}>
                <p><strong>{t('description')} : </strong>{selectedEvent.description}</p>
                <p><strong>{t('start')} : </strong>{moment(selectedEvent.start).format('LLLL')}</p>
                <p><strong>{t('end')} : </strong>{moment(selectedEvent.end).format('LLLL')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1050,
};

const dialogStyle = {
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.2)',
  width: '90%',
  maxWidth: '600px',
  padding: '20px',
};

const contentStyle = {
  borderRadius: '10px',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #ddd',
  paddingBottom: '10px',
};

const titleStyle = {
  margin: 0,
  fontSize: '18px',
  fontWeight: 'bold',
  textAlign: 'center',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
};

const bodyStyle = {
  paddingTop: '10px',
  lineHeight: '1.5',
  textAlign: 'center',
};
